<template>
  <v-container id="fcreate" fluid>
    <breadcrumbs />
    <h1 class="mb-2">
      {{ title }}
    </h1>

    <v-form ref="form" v-model="valid">
      <v-container fluid>
        <v-row>
          <v-col cols="12" md="7">
            <v-card>
              <v-card-text>
                <v-text-field
                  v-model="entity.name"
                  outlined
                  label="Название"
                  required
                  :rules="rules.required"
                  :loading="isLoading"
                  :error-messages="getErrorMsg('name')"
                />
                <v-autocomplete
                  v-model="entity.sourceId"
                  :items="sources"
                  label="Источники"
                  outlined
                  item-text="name"
                  item-value="id"
                  :loading="isLoading || isSourcesFetching"
                  :rules="rules.required"
                />
                <select-field
                  v-model="entity.type"
                  label="Тип"
                  item-title="name"
                  item-value="type"
                  :items="types"
                  outlined
                  :loading="isTypesFetching || isLoading"
                  @change="handleTypeChange"
                />
                <template v-if="entityId">
                  <fields-group
                    :loading="isLoading || isTypesFetching"
                    label="Доступы коннектора в FAKT"
                  >
                    <div
                      v-for="configItem in entity.credentials"
                      :key="`credentials_${configItem.key}`"
                    >
                      <v-text-field
                        v-if="configItem.type === ConfigItemType.String"
                        v-model="configItem.value"
                        outlined
                        :readonly="isConfigItemReadonly(configItem)"
                        :rules="isConfigItemReadonly(configItem) ? [] : rules.required"
                        :label="configItem.name"
                        :loading="isLoading"
                      />
                      <v-checkbox
                        v-else-if="configItem.type === ConfigItemType.Boolean"
                        v-model="configItem.value"
                        :label="configItem.name"
                        :loading="isLoading"
                        :readonly="isConfigItemReadonly(configItem)"
                        hide-details
                        true-value="1"
                        false-value="0"
                      />
                    </div>
                  </fields-group>
                  <fields-group
                    :loading="isLoading || isTypesFetching"
                    label="Vendor API"
                  >
                    <div
                      v-for="configItem in entity.config"
                      :key="`config_${configItem.key}`"
                    >
                      <v-text-field
                        v-if="configItem.type === ConfigItemType.String"
                        v-model="configItem.value"
                        outlined
                        :readonly="isConfigItemReadonly(configItem)"
                        :label="configItem.name"
                        :loading="isLoading"
                      />
                      <v-checkbox
                        v-else-if="configItem.type === ConfigItemType.Boolean"
                        v-model="configItem.value"
                        :label="configItem.name"
                        :readonly="isConfigItemReadonly(configItem)"
                        :loading="isLoading"
                        hide-details
                        true-value="1"
                        false-value="0"
                      />
                    </div>
                  </fields-group>
                </template>
              </v-card-text>

              <v-card-text>
                <v-row v-if="entityId">
                  <v-col cols="4">
                    <v-btn
                      class="mb-2"
                      block
                      large
                      color="primary"
                      :loading="isLoading"
                      @click.prevent="editEntity({ redirect: true })"
                    >
                      Сохранить
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn
                      class="mb-2"
                      block
                      large
                      color="success"
                      :loading="isLoading"
                      @click.prevent="editEntity({ redirect: false })"
                    >
                      Применить
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn
                      block
                      large
                      color="error"
                      :disabled="isLoading"
                      @click.prevent="goToListing"
                    >
                      Отменить
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col>
                    <v-btn
                      block
                      class="mb-2"
                      large
                      color="primary"
                      :loading="isLoading"
                      @click="createEntity"
                    >
                      Создать
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      block
                      large
                      color="error"
                      :loading="isLoading"
                      @click.prevent="$router.back()"
                    >
                      Отмена
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
  import SourceDataService from '@/services/api/SourceDataService'
  import ConnectorDataService from '@/services/api/ConnectorDataService'
  import ConnectorType from '@/models/enums/ConnectorType'
  import rules from '@/mixins/rules'
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import SelectField from '@/components/ui/selects/SelectField.vue'
  import connectorsApi from '@/api/connectors'
  import FieldsGroup from '@/components/ui/FieldsGroup.vue'
  import ConfigItemType from '@/models/enums/connector/ConfigItemType'

  export default {
    name: 'ConnectorForm',
    components: { FieldsGroup, SelectField },
    mixins: [rules, mixinSnackbarMessage],
    data () {
      return {
        title: 'Новый коннектор',
        dataService: ConnectorDataService,
        isSourcesFetching: false,
        isTypesFetching: false,
        isLoading: false,
        sources: [],
        types: [],
        valid: true,
        originalEntity: null,
        errorMessages: {},
        entity: {
          name: null,
          type: ConnectorType.YandexFood,
          sourceId: null,
          config: [],
          credentials: [],
        },
      }
    },
    computed: {
      ConfigItemType () {
        return ConfigItemType
      },
      entityId () {
        return this.$route.params.id
      },
    },
    watch: {
      '$route.params.id' (val) {
        if (val !== 'create') {
          this.initEntity()
        }
      },
    },
    created () {
      this.fetchSources()
      this.fetchTypes().then(() => {
        this.initEntity()
      })
    },
    methods: {
      isConfigItemReadonly (item) {
        return item.readonly && item.readonly === '1'
      },
      getErrorMsg (key) {
        return this.errorMessages[key] ? this.errorMessages[key] : ''
      },
      initEntity () {
        this.fillEntity()

        if (this.entityId) {
          this.title = 'Редактирование коннектора'
          this.fetchConnector()
        }
      },
      async fetchConnector () {
        this.isLoading = true
        try {
          const response = await ConnectorDataService.getOne(this.entityId)
          this.originalEntity = response
          this.fillEntity(response)
        } catch (e) {
          this.showError(e)
        } finally {
          this.isLoading = false
        }
      },
      fillEntity (entity = null) {
        this.entity = {
          name: entity?.name,
          type: entity?.type || ConnectorType.YandexFood,
          sourceId: entity?.sourceId || null,
          config: entity?.config?.length ? entity.config.slice() : [],
          credentials: entity?.credentials?.length
          ? entity.credentials.slice()
          : [],
        }

        const type = this.types.find((item) => item.type === this.entity.type)

        if (!type) {
          return
        }

        if (this.entity.config.length) {
          this.entity.config = this.fillConfig(
            type.config,
            this.entity.config,
          ).slice()
        } else {
          this.entity.config = type.config.slice()
        }

        if (this.entity.credentials.length) {
          this.entity.credentials = this.fillConfig(
            type.credentials,
            this.originalEntity.credentials,
          ).slice()
        } else {
          this.entity.credentials = type.credentials.slice()
        }
      },
      handleTypeChange () {
        const type = this.types.find((item) => item.type === this.entity.type)

        if (!type) {
          return
        }

        if (!this.originalEntity || type.type !== this.originalEntity.type) {
          this.entity.config = type.config.slice()
          this.entity.credentials = type.credentials.slice()
        } else {
          this.fillEntityConfig(type)
          this.fillEntityCredentials(type)
        }
      },
      fillEntityConfig (type) {
        if (this.originalEntity.config.length) {
          this.entity.config = []
          this.entity.config = this.fillConfig(
            type.config,
            this.originalEntity.config,
          ).slice()
        } else {
          this.entity.config = type.config.slice()
        }
      },
      fillEntityCredentials (type) {
        if (this.originalEntity.credentials.length) {
          this.entity.credentials = []
          this.entity.credentials = this.fillConfig(
            type.credentials,
            this.originalEntity.credentials,
          ).slice()
        } else {
          this.entity.credentials = type.credentials.slice()
        }
      },
      fillConfig (config, entityConfig) {
        const result = []

        config.forEach((item) => {
          const entityConfigItem = entityConfig.find((i) => i.key === item.key)

          const data = entityConfigItem
            ? { ...item, value: entityConfigItem.value }
            : { ...item }

          result.push(data)
        })

        return result
      },
      async fetchTypes () {
        this.isTypesFetching = true
        try {
          const response = await connectorsApi.fetchTypes()
          this.types = response.items
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.isTypesFetching = false
        }
      },
      async fetchSources () {
        this.isSourcesFetching = true
        try {
          this.sources = await SourceDataService.getList()
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.isSourcesFetching = false
        }
      },
      async createEntity () {
        this.errorMessages = {}
        const valid = this.$refs.form.validate()

        if (!valid) {
          this.showSnackbar()
          return
        }

        this.isLoading = true

        try {
          const savedData = await ConnectorDataService.createWithFullResponse(this.entity)
          this.showSuccessMessage('Изменения сохранены')
          this.$router.push(`/connectors/${savedData.id}`)
        } catch (e) {
          this.showError(e)
        } finally {
          this.isLoading = false
        }
      },
      async editEntity ({ redirect = false }) {
        this.errorMessages = {}
        const valid = this.$refs.form.validate()

        if (!valid) {
          this.showSnackbar()
          return
        }

        this.isLoading = true
        try {
          const savedData = await ConnectorDataService.update(
            this.entityId,
            this.entity,
          )
          this.showSuccessMessage('Изменения сохранены')

          if (redirect) {
            this.goToListing()
          } else {
            this.originalEntity = savedData
            this.fillEntity(savedData)
          }
        } catch (err) {
          this.showError(err)
        } finally {
          this.isLoading = false
        }
      },
      showError (err) {
        this.showErrorFromResponse(err)

        if (err.response && err.response.data && err.response.data.data) {
          this.errorMessages = err.response.data.data
          this.showSnackbar()
        }
      },
      showSnackbar () {
        this.snackbar = true
        document.getElementById('fcreate').scrollIntoView({
          behavior: 'smooth',
        })

        setTimeout(() => {
          this.snackbar = false
        }, 3000)
      },
      goToListing () {
        this.$router.push('/connectors')
      },
    },
  }
</script>
